import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { getUserDetail } from "@/lib/admin.functions";
import { PaypalSubscriptionPanel } from "@/components/admin/PaypalSubscriptionPanel";
import { ArrowLeft, Activity, MessageSquare, Mail } from "lucide-react";

export const Route = createFileRoute("/admin_/users/$userId")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "User detail — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: UserDetailPage,
});

function Stat({ icon: Icon, label, value }: { icon: any; label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 font-display text-3xl text-foreground">{value}</div>
    </div>
  );
}

function UserDetailPage() {
  const { userId } = Route.useParams();
  const fetcher = useServerFn(getUserDetail);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "user", userId],
    queryFn: () => fetcher({ data: { userId } }),
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <Link
        to="/admin"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to admin
      </Link>

      {isLoading || !data ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : !data.profile ? (
        <p className="text-muted-foreground">User not found.</p>
      ) : (
        <div className="space-y-8">
          <header className="rounded-2xl border border-gold/20 bg-card p-6">
            <h1 className="font-display text-2xl text-foreground">
              {data.profile.display_name ?? "Unnamed user"}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {data.profile.membership_tier} · joined{" "}
              {new Date(data.profile.joined_at).toLocaleDateString()}
            </p>
            {data.profile.focus_areas?.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {data.profile.focus_areas.map((f: string) => (
                  <span key={f} className="rounded-full bg-gold/10 px-2.5 py-0.5 text-xs text-foreground/80">
                    {f}
                  </span>
                ))}
              </div>
            ) : null}
          </header>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Stat icon={Activity} label="Exercise sessions" value={data.stats.sessionCount} />
            <Stat icon={MessageSquare} label="Community posts" value={data.stats.postCount} />
          </div>

          <PaypalSubscriptionPanel userId={userId} />


          <section className="rounded-2xl border border-gold/20 bg-card p-6">
            <h2 className="font-display text-lg text-foreground">Private wellbeing content</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Journal entries, dream journals, and mood check-ins are confidential.
              Admins do not have access to this content. If a member's entry triggers a
              high-risk safeguarding flag, it will appear in the{" "}
              <Link to="/admin/crisis-alerts" className="font-semibold text-[var(--gold-deep)] underline">
                Safety Alerts
              </Link>{" "}
              queue with the minimum information required.
            </p>
          </section>

          <section className="rounded-2xl border border-gold/20 bg-card p-6">
            <h2 className="font-display text-lg text-foreground">Recent exercise sessions</h2>
            <div className="mt-3 divide-y divide-border/40">
              {data.recentSessions.length === 0 && (
                <p className="py-3 text-sm text-muted-foreground">No sessions.</p>
              )}
              {data.recentSessions.map((s: any) => (
                <div key={s.id} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="capitalize text-foreground">{s.exercise_slug.replace(/-/g, " ")}</span>
                  <span className="text-xs text-muted-foreground">
                    {s.completed_at ? "Completed" : "In progress"} ·{" "}
                    {new Date(s.started_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
